import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { getListingSpecialDeals } from 'common/apartmentHelpers';
import { makeListingFullAddressString, getListingManagedPhotoUrls } from 'common/listingHelpers';
import { formatListingAddress } from 'common/formatters';

import getGoogleMapsDirectionsUrl from 'common/util/getGoogleMapsDirectionsUrl';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import { BUTTON_THEME_GREEN } from 'client/components/ButtonDeprecated';
import ListingCard from 'client/components/ListingCard';
import SrExternalLink from 'client/components/SrExternalLink';
import SrListingSelfTouringSection from 'client/components/SrListingSelfTouringSection';
import SrImagePreloader from 'client/components/SrImagePreloader';

import SelfTourMultiFamilyPageLayout from './SelfTourMultiFamilyPageLayout';

const SelfTourMultiFamilyPage = ({
  listingPhotoResolution,
  stepId,
  apartmentSpecialDeals,
  currentUser,
  listing,
  apartment,
  apartmentBuilding,
  history,
  location,
  dispatch,
}) => {
  const specialDeals = getListingSpecialDeals({
    apartmentSpecialDeals,
    listing,
  });
  const listingPhotos = getListingManagedPhotoUrls({ listing });
  const steps = [];

  steps.push({
    id: 'start',
    stepNumber: 1,
    stepName: 'Drive to the Property',
    photoUrls: apartment.streetEntrancePhotoUrls,
    buttonCTAText: 'I’m at the property',
    mediaRender: () => {
      return (
        <div className="buildingBox">
          <SrText font="greycliff" weight="800" size="31" display="block" lineHeight="37px">
            Building {apartmentBuilding.buildingNumber}
          </SrText>
          <SrText font="greycliff" weight="800" size="21" display="block" className="apartmentUnit">
            Unit {listing.apartmentUnit}
          </SrText>
        </div>
      );
    },
    contentRender: () => {
      return (
        <SrBox display="grid">
          <SrText font="greycliff" weight="500" className="address">
            {`📍 ${makeListingFullAddressString(listing)}`}
          </SrText>
          <SrExternalLink
            link={getGoogleMapsDirectionsUrl({
              directions: [formatListingAddress(listing, { hideApartmentNumber: true })],
            })}
            className="SrText text-align-center"
          >
            <SrText font="greycliff" weight="800" color="blue300">
              Get Directions
            </SrText>
          </SrExternalLink>
        </SrBox>
      );
    },
  });

  if (apartment.hasGateCode) {
    steps.push({
      id: 'gate-code',
      stepNumber: steps.length + 1,
      stepName: 'Enter Gate Code',
      photoUrls: apartment.gatePhotoUrls,
      code: apartment.gateCode,
      buttonCTAText: 'Next: Park Car',
    });
  }

  steps.push({
    id: 'park-car',
    stepNumber: steps.length + 1,
    stepName: 'Park car',
    photoUrls: apartment.parkingPhotoUrls,
    description: apartment.parkingInstructions,
    buttonCTAText: apartmentBuilding.hasAccessCode
      ? 'Next: Access The Building'
      : 'Next: Walk To Unit',
  });

  if (apartmentBuilding.hasAccessCode) {
    steps.push({
      id: 'building-access-code',
      stepNumber: steps.length + 1,
      stepName: 'Access The Building',
      photoUrls: apartmentBuilding.accessPhotoUrls,
      code: apartmentBuilding.accessCode,
      description: apartmentBuilding.accessInstructions,
      buttonCTAText: 'Next: Walk To Unit',
    });
  }

  steps.push({
    id: 'walk-to-unit',
    stepNumber: steps.length + 1,
    stepName: 'Walk to Unit',
    photoUrls: listing.walkToUnitPhotoUrls,
    description: listing.walkToUnitInstructions,
    buttonDOM: (
      <SrListingSelfTouringSection
        buttonTextOverride={"I'm at the unit now"}
        listing={listing}
        dispatch={dispatch}
        currentUser={currentUser}
        history={history}
        hideInstructions
        isApartmentFlow
        onCreateSelfTour={(tourId) => {
          history.push(`/listing/${listing.id}/self-tour/unit?tourId=${tourId}`);
        }}
      />
    ),
  });

  steps.push({
    id: 'unit',
    stepName: 'Tour the unit',
    photoUrls: [listingPhotos[0]],
    mediaRender: () => (
      <SrFlex justify="center" align="center" height="100%" className="listingCardContainer">
        <ListingCard
          enableBookmarkButton
          useSrExternalLink
          viewText="View all details"
          specialDeals={specialDeals}
          listing={listing}
          currentUser={currentUser}
          photoResolution={listingPhotoResolution}
          showRequestShowingButton={false}
          onClick={() => {}}
        />
      </SrFlex>
    ),
    description:
      'Take your time and tap "View all details" above to learn more about the unit. ' +
      (specialDeals.length
        ? `Special${specialDeals.length > 1 ? 's are' : ' is'} **${specialDeals
            .map(({ headline }) => headline)
            .join(', ')}**`
        : ''),
    buttonCTAText: 'Finish Self Tour',
    buttonCTATheme: BUTTON_THEME_GREEN,
  });

  const currentStepIndex = Math.max(
    steps.findIndex((step) => step.id === stepId),
    0,
  );
  const currentStep = steps[currentStepIndex];
  const nextStep = steps[currentStepIndex + 1];

  const onClickExit =
    currentStepIndex !== 0
      ? null
      : () => {
          history.push(`/listing/${listing.id}`);
        };

  const onClickGoBack =
    currentStepIndex === 0
      ? null
      : () => {
          const previousStepIndex = currentStepIndex - 1;
          const previousStep = steps[previousStepIndex];
          if (previousStep) {
            history.push(`/listing/${listing.id}/self-tour/${previousStep.id}`);
          }
        };

  const onClickButtonCTA = () => {
    const nextStepIndex = currentStepIndex + 1;
    const nextStep = steps[nextStepIndex];
    if (nextStep) {
      history.push(`/listing/${listing.id}/self-tour/${nextStep.id}`);
    } else {
      const locationSearch = queryString.parse(location.search || '');
      history.push(`/listing/${listing.id}?rateTourId=${locationSearch.tourId}`);
    }
  };

  return (
    <div className="SelfTourMultiFamilyPage">
      <SelfTourMultiFamilyPageLayout
        {...currentStep}
        percentage={((currentStepIndex + 1) * 100) / (steps.length + 1)}
        apartmentName={apartment.name}
        onClickExit={onClickExit}
        onClickGoBack={onClickGoBack}
        onClickButtonCTA={onClickButtonCTA}
      />
      {nextStep && nextStep.photoUrls && <SrImagePreloader imageUrls={nextStep.photoUrls} />}
    </div>
  );
};

SelfTourMultiFamilyPage.propTypes = {
  listingPhotoResolution: PropTypes.number.isRequired,
  stepId: PropTypes.string,
  apartmentSpecialDeals: PropTypes.array,
  currentUser: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  apartment: PropTypes.object.isRequired,
  apartmentBuilding: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SelfTourMultiFamilyPage.defaultProps = {
  stepId: 'start',
  apartmentSpecialDeals: [],
};

SelfTourMultiFamilyPage.displayName = 'SelfTourMultiFamilyPage';

export default SelfTourMultiFamilyPage;
